import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import apiInstance from '@/utils/api'
import { useEffectiveRole } from '@/composables/useEffectiveRole'

export interface Company {
  value: number
  title: string
  partner_id: number
}

export const useCompaniesStore = defineStore('companies', () => {
  const companies = ref<Company[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const selectedPartnerId = ref<number | null>(null)
  const isLoaded = ref(false)

  const filteredCompanies = computed(() => {
    const { isPartnerLevel } = useEffectiveRole()

    // Partners (and partner-created employees) see all companies
    if (isPartnerLevel.value) {
      return companies.value
    }

    // Otherwise, filter by selected partner ID
    const partnerId = selectedPartnerId.value ?? 0
    return companies.value.filter((company) => company.partner_id === partnerId)
  })

  const fetchCompanies = async () => {
    // Skip if already loaded successfully
    if (isLoaded.value && companies.value.length > 0) {
      return
    }

    loading.value = true
    error.value = null

    try {
      const response = await apiInstance.get('/get-companies')

      if (response.data.error === false && response.data.data) {
        companies.value = response.data.data
        isLoaded.value = true
      } else {
        error.value = response.data.message || 'Failed to fetch companies'
        isLoaded.value = false
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Network error while fetching companies'
      isLoaded.value = false
    } finally {
      loading.value = false
    }
  }

  const refreshCompanies = async () => {
    // Force refresh by resetting the loaded flag
    isLoaded.value = false
    await fetchCompanies()
  }

  const setSelectedPartnerId = (partnerId: number | null) => {
    selectedPartnerId.value = partnerId
  }

  const reset = () => {
    companies.value = []
    loading.value = false
    error.value = null
    selectedPartnerId.value = null
    isLoaded.value = false
  }

  return {
    companies,
    filteredCompanies,
    loading,
    error,
    selectedPartnerId,
    isLoaded,
    fetchCompanies,
    refreshCompanies,
    setSelectedPartnerId,
    reset,
  }
})
